import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { useMeals } from "../contexts/UserMealsContext";
import { useMealTags } from "../contexts/MealTagContext";
import { useIngredientUnits } from "../contexts/IngredientUnitContext";
import { UserMealForm } from "../components/UserMealPage/UserMealForm";
import { MealFormSkeleton } from "../components/UserMealPage/MealFormSkeleton";
import { LoadingComponent } from "../components/LoadingComponent";

export function EditMealPage() {
  const navigate = useNavigate();
  const { replaceMeal, fetchMealById } = useMeals();
  const { mealTags } = useMealTags();
  const { ingredientUnits } = useIngredientUnits();
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    const fetchMeal = async () => {
      const mealToEdit = await fetchMealById(id);

      const mealTagList = mealToEdit.mealTagList.map((assignment) => ({
        id: assignment.id,
        tagId: assignment.tagId,
        tagContent: assignment.tagContent,
      }));

      const ingredientList = mealToEdit.ingredientList.map((ingredient) => ({
        id: ingredient.ingredientId,
        name: ingredient.ingredientName,
        quantity: ingredient.ingredientQuantity,
        unit: ingredient.ingredientUnit,
      }));

      const currentState = {
        mealName: mealToEdit.mealName,
        mealCategories: {
          breakfast: mealToEdit.breakfast,
          lunch: mealToEdit.lunch,
          dinner: mealToEdit.dinner,
          snack: mealToEdit.snack,
        },
        mealTagList,
        timeMins: mealToEdit.timeMins || "",
        servings: mealToEdit.servings || "",
        ingredients: ingredientList,
      };

      setForm(currentState);
    };

    fetchMeal();
  }, [id, fetchMealById]);

  if (!form)
    return (
      <div className="h-screen flex flex-col overflow-hidden">
        <MealFormSkeleton heading="Edit Meal" />
      </div>
    );

  const handleSubmit = async (form) => {
    setIsLoading(true);

    try {
      const mealTagList = form.mealTagList.map((tag) =>
        tag.id ? { id: tag.id, tagId: tag.tagId } : { tagId: tag.tagId },
      );

      const ingredientList = form.ingredients.map((ing) => {
        const unitObj = ingredientUnits.find((u) => u.unit === ing.unit);
        return {
          ingredientId: ing.id,
          ingredientName: ing.name.trim(),
          ingredientQuantity: ing.quantity,
          unitId: unitObj?.unitId ?? null,
        };
      });

      const editMealRequest = {
        mealName: form.mealName,
        breakfast: form.mealCategories.breakfast,
        lunch: form.mealCategories.lunch,
        dinner: form.mealCategories.dinner,
        snack: form.mealCategories.snack,
        timeMins: form.timeMins,
        servings: form.servings,

        ingredientList: ingredientList,
        mealTagList,
      };

      await replaceMeal(Number(id), editMealRequest);
      navigate("/MealList");
    } catch (err) {
      console.error("Failed to edit meal: ", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {isLoading ? (
        <LoadingComponent message={"Updating your meal..."} />
      ) : (
        <UserMealForm
          onSubmit={handleSubmit}
          onCancel={() => navigate("/MealList")}
          mealData={form}
          heading={"Edit Meal"}
          confirmButtonText={"Edit Meal"}
          userMealTags={mealTags}
          ingredientUnits={ingredientUnits.map((u) => u.unit)}
        />
      )}
    </div>
  );
}
